import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import classNames from 'classnames';
import { Paintbrush } from 'lucide-react';

import { Button, ColorInput, Pressable } from '../../../shared/ui';

import { Figure, PlacedFigure, getFigureSize } from './types';
import { FigureRenderer } from './FigureRenderer';
import type { SandTherapyBoardProps } from './SandTherapyBoard';

/** Множитель размера фигуры на поле (как на десктопе) */
const PLACED_SCALE = 2;
/**
 * Ширина поля, при которой фигура на телефоне занимает столько же места в долях поля, сколько на десктопе при scale=1.
 * Размеры фигур заданы в px, а позиции — в %, поэтому на узком поле фигуры масштабируем пропорционально ширине.
 */
const REFERENCE_BOARD_WIDTH = 700;
const MIN_SCALE = 0.3;
const MAX_SCALE = 3;
const SCALE_STEP = 0.2;
/** Не чаще раза в 50 мс отправляем положение перетаскиваемой фигуры собеседнику */
const MOVE_SEND_INTERVAL_MS = 50;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

interface IActionButtonProps {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
}

const ActionButton: FC<IActionButtonProps> = ({ label, disabled, onClick, children, danger = false }) => (
  <Pressable
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className={classNames(
      'flex h-11 flex-col items-center justify-center rounded-control text-body-xl font-medium leading-none transition-colors',
      disabled
        ? 'bg-white/10 text-white/30'
        : danger
          ? 'bg-white text-danger active:bg-danger-surface'
          : 'bg-white text-ink-muted active:bg-neutral',
    )}
  >
    {children}
  </Pressable>
);

/**
 * Поле песочной терапии для телефона: без HTML5 drag-and-drop (на касаниях он не работает).
 * Фигура ставится тапом по ленте, перемещается пальцем (pointer events), а поворот, размер и удаление выбранной
 * фигуры — кнопками под полем (на touch нет hover, на который рассчитан десктоп).
 */
export const SandTherapyBoardMobile: FC<SandTherapyBoardProps> = ({
  selectedFigures,
  placedFigures,
  onPlaceFigure,
  onMoveFigure,
  onRotateFigure,
  onResizeFigure,
  onRemoveFigure,
  onSandColorChange,
  onClientReady,
  sandColor = '#fef3c7',
  clientReady = false,
  readonly = false,
}) => {
  const boardRef = useRef<HTMLDivElement>(null);
  const [boardWidth, setBoardWidth] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragRef = useRef<{ id: string; pointerId: number } | null>(null);
  const lastSentAtRef = useRef(0);
  const pendingMoveRef = useRef<{ id: string; x: number; y: number } | null>(null);
  const sendTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = boardRef.current;
    if (!el) return;

    const update = () => setBoardWidth(el.clientWidth);
    update();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);

      return () => window.removeEventListener('resize', update);
    }

    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (sendTimerRef.current !== null) window.clearTimeout(sendTimerRef.current);
    },
    [],
  );

  // Выбранную фигуру могли удалить (в том числе с другой стороны) — снимаем выбор
  const activeFigure = placedFigures.find((f) => f.id === activeId) ?? null;
  useEffect(() => {
    if (activeId && !activeFigure) setActiveId(null);
  }, [activeId, activeFigure]);

  const unit = boardWidth > 0 ? boardWidth / REFERENCE_BOARD_WIDTH : 1;
  const figurePx = (figure: PlacedFigure) =>
    Math.round(getFigureSize(figure.size) * PLACED_SCALE * (figure.scale ?? 1) * unit);

  const getRelativePos = useCallback((clientX: number, clientY: number) => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 50, y: 50 };

    return {
      x: clamp(((clientX - rect.left) / rect.width) * 100, 5, 95),
      y: clamp(((clientY - rect.top) / rect.height) * 100, 5, 95),
    };
  }, []);

  const addFigure = (figure: Figure) => {
    // Небольшой разброс вокруг центра, чтобы несколько фигур подряд не слипались в одну точку
    const jitter = () => 50 + (Math.random() - 0.5) * 16;
    const placed: PlacedFigure = {
      ...figure,
      id: `${figure.id}-placed-${Date.now()}-${Math.random()}`,
      x: jitter(),
      y: jitter(),
      rotation: 0,
      scale: 1,
    };
    onPlaceFigure(placed);
    setActiveId(placed.id);
  };

  const flushMove = () => {
    if (sendTimerRef.current !== null) {
      window.clearTimeout(sendTimerRef.current);
      sendTimerRef.current = null;
    }
    const pending = pendingMoveRef.current;
    if (pending) {
      onMoveFigure(pending.id, pending.x, pending.y);
      pendingMoveRef.current = null;
      lastSentAtRef.current = Date.now();
    }
  };

  const handleFigurePointerDown = (e: ReactPointerEvent<HTMLDivElement>, figure: PlacedFigure) => {
    if (readonly) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { id: figure.id, pointerId: e.pointerId };
    setActiveId(figure.id);
  };

  const handleFigurePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const { x, y } = getRelativePos(e.clientX, e.clientY);
    pendingMoveRef.current = { id: drag.id, x, y };

    const sinceLast = Date.now() - lastSentAtRef.current;
    if (sinceLast >= MOVE_SEND_INTERVAL_MS) {
      flushMove();
    } else if (sendTimerRef.current === null) {
      sendTimerRef.current = window.setTimeout(flushMove, MOVE_SEND_INTERVAL_MS - sinceLast);
    }
  };

  const handleFigurePointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    flushMove();
    dragRef.current = null;
  };

  const changeScale = (delta: number) => {
    if (!activeFigure) return;
    onResizeFigure(activeFigure.id, clamp((activeFigure.scale ?? 1) + delta, MIN_SCALE, MAX_SCALE));
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-2">
      <div
        ref={boardRef}
        className="relative min-h-0 flex-1 touch-none select-none overflow-hidden rounded-lg"
        style={{
          backgroundImage: `
            radial-gradient(ellipse at 30% 40%, ${sandColor}ee, ${sandColor}aa),
            repeating-linear-gradient(45deg, rgba(0,0,0,0.03) 0px, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 8px)
          `,
        }}
        onPointerDown={() => setActiveId(null)}
      >
        {placedFigures.length === 0 && !readonly && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6">
            <p className="text-center text-ui text-ink-muted">Нажмите на фигуру внизу, чтобы поставить её на поле</p>
          </div>
        )}

        {placedFigures.map((fig) => {
          const px = figurePx(fig);
          const isActive = !readonly && fig.id === activeId;

          return (
            <div
              key={fig.id}
              data-testid="placed-figure"
              className="absolute touch-none"
              style={{
                left: `${fig.x}%`,
                top: `${fig.y}%`,
                width: px,
                height: px,
                transform: `translate(-50%, -50%) rotate(${fig.rotation}deg)`,
                zIndex: isActive ? 20 : 10,
                outline: isActive ? '2px dashed rgba(255,255,255,0.95)' : 'none',
                outlineOffset: 2,
                borderRadius: 8,
              }}
              onPointerDown={(e) => handleFigurePointerDown(e, fig)}
              onPointerMove={handleFigurePointerMove}
              onPointerUp={handleFigurePointerEnd}
              onPointerCancel={handleFigurePointerEnd}
            >
              <FigureRenderer figure={fig} className="pointer-events-none h-full w-full" />
            </div>
          );
        })}

        {onSandColorChange && !readonly && (
          <div className="absolute right-2 top-2 z-30" onPointerDown={(e) => e.stopPropagation()}>
            <ColorInput
              label="Изменить цвет песка"
              value={sandColor}
              onChange={onSandColorChange}
              className="rounded-full"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-white/50 shadow-overlay backdrop-blur-sm">
                <Paintbrush className="h-5 w-5 text-ink-muted" strokeWidth={2} />
              </div>
            </ColorInput>
          </div>
        )}
      </div>

      {!readonly && (
        <>
          <div className="shrink-0">
            <p className="mb-1 text-ui-xs text-white/70">Нажмите на фигуру, чтобы добавить её на поле</p>
            <div className="flex gap-2 overflow-x-auto rounded-control bg-white/10 p-2">
              {selectedFigures.map((fig) => {
                const size = getFigureSize(fig.size);

                return (
                  <Pressable
                    key={fig.id}
                    aria-label={`Добавить: ${fig.name}`}
                    title={fig.name}
                    onClick={() => addFigure(fig)}
                    className="flex shrink-0 items-center justify-center rounded-control bg-white/10 p-1 active:bg-white/25"
                    style={{ width: Math.max(48, size * 0.8), height: 56 }}
                  >
                    <FigureRenderer figure={fig} className="h-full w-full" />
                  </Pressable>
                );
              })}
            </div>
          </div>

          <div className="grid shrink-0 grid-cols-4 gap-2">
            <ActionButton
              label="Повернуть"
              disabled={!activeFigure}
              onClick={() => activeFigure && onRotateFigure(activeFigure.id)}
            >
              ↻
            </ActionButton>
            <ActionButton label="Уменьшить" disabled={!activeFigure} onClick={() => changeScale(-SCALE_STEP)}>
              −
            </ActionButton>
            <ActionButton label="Увеличить" disabled={!activeFigure} onClick={() => changeScale(SCALE_STEP)}>
              +
            </ActionButton>
            <ActionButton
              label="Удалить"
              danger
              disabled={!activeFigure}
              onClick={() => {
                if (!activeFigure) return;
                onRemoveFigure(activeFigure.id);
                setActiveId(null);
              }}
            >
              ✕
            </ActionButton>
          </div>

          {onClientReady && (
            <Button size="lg" fullWidth className="shrink-0" disabled={clientReady} onClick={onClientReady}>
              {clientReady ? 'Готово ✓' : 'Готово'}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
